
import { useState, useCallback, useMemo } from 'react';
import GridLayout, { Layout } from 'react-grid-layout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui';
import 'react-grid-layout/css/styles.css';

export interface WidgetConfig {
  id: string;
  type: string;
  title: string;
  component: React.ComponentType<{ isEditMode?: boolean }>;
  defaultLayout: {
    x: number;
    y: number;
    w: number;
    h: number;
    minW?: number;
    minH?: number;
  };
}

export interface WidgetGridProps {
  widgets: WidgetConfig[];
  storageKey: string;
  columns?: number;
  rowHeight?: number;
}

export function WidgetGrid({
  widgets,
  storageKey,
  columns = 12,
  rowHeight = 100,
}: WidgetGridProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [removedWidgets, setRemovedWidgets] = useState<string[]>([]);

  // Load layout from localStorage or use defaults (Layout = array of layout items)
  const loadLayout = useCallback((): Layout => {
    if (typeof window === 'undefined') return [];
    
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved layout:', e);
      }
    }
    
    // Return default layout (cast for react-grid-layout type compatibility)
    return widgets.map((widget) => ({
      i: widget.id,
      x: widget.defaultLayout.x,
      y: widget.defaultLayout.y,
      w: widget.defaultLayout.w,
      h: widget.defaultLayout.h,
      minW: widget.defaultLayout.minW ?? 2,
      minH: widget.defaultLayout.minH ?? 2,
    })) as unknown as Layout;
  }, [storageKey, widgets]);

  const [layout, setLayout] = useState<Layout>(loadLayout);

  // Save layout to localStorage
  const saveLayout = useCallback(
    (newLayout: Layout) => {
      if (typeof window === 'undefined') return;
      localStorage.setItem(storageKey, JSON.stringify(newLayout));
      setLayout(newLayout);
    },
    [storageKey]
  );

  // Reset to default layout
  const resetLayout = useCallback(() => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(storageKey);
    setRemovedWidgets([]);
    setLayout(loadLayout());
    setIsEditMode(false);
  }, [storageKey, loadLayout]);

  // Remove widget
  const handleRemoveWidget = useCallback((widgetId: string) => {
    setRemovedWidgets((prev) => [...prev, widgetId]);
  }, []);

  // Add widget back
  const handleAddWidget = useCallback((widgetId: string) => {
    setRemovedWidgets((prev) => prev.filter((id) => id !== widgetId));
  }, []);

  // Filter visible widgets
  const visibleWidgets = useMemo(() => {
    return widgets.filter((w) => !removedWidgets.includes(w.id));
  }, [widgets, removedWidgets]);

  // Filter layout for visible widgets
  const visibleLayout = useMemo(() => {
    return layout.filter((l) => !removedWidgets.includes(l.i));
  }, [layout, removedWidgets]);

  return (
    <div className="space-y-4">
      {/* Edit mode controls */}
      <PageHeader
        title="Dashboard"
        actions={
          <div className="flex items-center gap-2">
            {isEditMode && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400">
                Edit Mode
              </span>
            )}
            {isEditMode && removedWidgets.length > 0 && (
            <div className="relative group">
              <Button variant="ghost" size="sm">
                Add Widget ({removedWidgets.length})
              </Button>
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                {widgets
                  .filter((w) => removedWidgets.includes(w.id))
                  .map((widget) => (
                    <button
                      key={widget.id}
                      onClick={() => handleAddWidget(widget.id)}
                      className="block w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 first:rounded-t-lg last:rounded-b-lg"
                    >
                      {widget.title}
                    </button>
                  ))}
              </div>
            </div>
          )}
          {isEditMode && (
            <Button variant="ghost" size="sm" onClick={resetLayout}>
              Reset Layout
            </Button>
          )}
          <Button
            variant={isEditMode ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setIsEditMode(!isEditMode)}
          >
            {isEditMode ? 'Done' : 'Edit Layout'}
          </Button>
          </div>
        }
      />

      {/* Grid layout - runtime API uses cols, rowHeight, etc. (@types/react-grid-layout may not match v2) */}
      <GridLayout
        {...({
          className: 'layout',
          layout: visibleLayout,
          cols: columns,
          rowHeight,
          width: 1200,
          isDraggable: isEditMode,
          isResizable: isEditMode,
          draggableHandle: '.widget-drag-handle',
          onLayoutChange: (newLayout: Layout) => {
            if (isEditMode) saveLayout(newLayout);
          },
          compactType: 'vertical',
          preventCollision: false,
        } as unknown as React.ComponentProps<typeof GridLayout>)}
      >
        {visibleWidgets.map((widget) => {
          const WidgetComponent = widget.component;
          return (
            <div key={widget.id}>
              <WidgetComponent isEditMode={isEditMode} />
            </div>
          );
        })}
      </GridLayout>

      {visibleWidgets.length === 0 && (
        <Card padding="none" className="text-center py-12">
          <p className="text-slate-500 dark:text-slate-400">
            No widgets to display. Click "Edit Layout" to add widgets.
          </p>
        </Card>
      )}
    </div>
  );
}
